import { XIcon } from "lucide-react-native";
import { type ReactElement, useCallback, useMemo } from "react";
import { Dimensions, StyleSheet } from "react-native";
import { ToastOptions, useToast } from "react-native-toast-notifications";
// Deep import because the package root re-exports `ToastOptions` but not
// `ToastProps` — the wider type a queued toast actually becomes. Naming the
// real type is still better than inventing a lookalike here: if the library
// changes the callback's argument, this breaks at compile time instead of
// drifting quietly.
import type { ToastProps } from "react-native-toast-notifications/lib/typescript/toast";

import { Icon } from "@/components/atoms/Icon";
import colors from "@/config/colors";

/**
 * Props for showing toast notifications.
 */
interface ShowToastProps extends Omit<ToastOptions, "type"> {
  title?: string;
  description?: string;
  /**
   * How this toast reads to the user. REQUIRED at the type level (SE-6889).
   *
   * It was optional, defaulting to `"success"`, and that default is what made
   * SE-6889 possible: `SignInContainer` reported a FAILED sign-in with
   * `show({ description })` and the user got a green success toast for 1500ms.
   * Being told, briefly and in green, that nothing went wrong is why the
   * reported retry ladder got exhausted.
   *
   * Requiring it costs nothing and was verified to cost nothing: at the time of
   * this change all 178 call sites in `src/` already passed it explicitly
   * except that one defect, and NO caller relied on the default deliberately
   * (every one of the 65 success toasts spells `action: "success"` out). So
   * the default was protecting no one and trapping the one caller who forgot.
   *
   * The runtime default is deliberately kept below as a backstop for callers
   * that reach `show` without type checking; this property is what stops a
   * typed caller from ever silently landing on it again.
   */
  action: "normal" | "success" | "warning" | "danger" | "custom";
  /**
   * Renders this one toast's body instead of the default text row.
   *
   * The library has always supported this per call — `show()` spreads its
   * options into the toast record, and the Toast component checks
   * `props.renderToast` before falling back to the stock layout. It was simply
   * unreachable from here, because `ToastOptions` does not declare it;
   * `ToastProps`, the wider type the record actually becomes, does.
   *
   * Declaring it is what lets a caller mount an interactive toast — SE-6981's
   * add-player undo is the first — without a root-layout change or a cast.
   */
  renderToast?: (toast: ToastProps) => ReactElement;
}

/**
 * Custom hook for displaying toast notifications with consistent styling.
 * @remarks
 * Wraps react-native-toast-notifications to provide application-specific toast
 * configuration including colors, icons, and default messages for errors.
 * @returns Toast notification API with custom show function
 * @example
 * ```typescript
 * const toast = useToastNotification();
 *
 * // Success toast
 * toast.show({ description: 'Player added', action: 'success' });
 *
 * // Error toast with default message
 * toast.show({ action: 'danger' });
 * // Displays: "There has been an error, please try again"
 * ```
 */

export const useToastNotification = () => {
  const toast = useToast();

  const show = useCallback(
    (props: ShowToastProps) => {
      const { description, action = "success", ...rest } = props;
      const descriptionWithDefaultErrorText =
        action === "danger" && !description
          ? "There has been an error, please try again"
          : description;

      toast.show(descriptionWithDefaultErrorText ?? "", {
        type: action,
        placement: "top",
        duration: 1500,
        animationType: "slide-in",
        normalColor: colors.toast.normal,
        dangerColor: colors.toast.danger,
        successColor: colors.toast.success,
        warningColor: colors.toast.warning,
        style: style.container,
        textStyle: style.text,
        iconClose: (
          <Icon as={XIcon} size="xl" className="text-content-on-accent" />
        ),
        ...rest,
      } as ToastOptions);
    },
    [toast]
  );

  return useMemo(
    () => ({
      ...toast,
      show,
    }),
    [toast, show]
  );
};

const style = StyleSheet.create({
  container: {
    width: Dimensions.get("window").width,
    maxWidth: undefined,
    minHeight: 68,
    justifyContent: "center",
    gap: 8,
    padding: 16,
  },
  text: {
    color: colors.toast.text,
    fontSize: 16,
    fontWeight: "400",
    fontFamily: "Satoshi",
    lineHeight: 24,
  },
  textMedium: {
    fontWeight: "500",
    fontFamily: "Satoshi Medium",
  },
});
